import { graphql, useStaticQuery } from "gatsby";
import * as React from "react";

import { SITE_URL } from "../../../../company/settings/metadata";
import { DEFAULT_LANGUAGE } from "../../../../company/settings/translations";
import DoNotMountComponent from "../DoNotMountComponent/DoNotMountComponent";
import { cleanSlashInPath } from "../shared/utils/cleanSplash";
import { getAbsoluteUrl } from "../shared/utils/getAbsoluteUrl";
import { getCodeLang } from "../shared/utils/getCodeLang";
import { getSlugWithLang } from "../shared/utils/getSlugWithLang";
import { getSocialMetaTag } from "./getSocialMetaTag";
import { ISEO } from "./iseo";

const titleDefault = "thecookies";
const descriptionDefault = "thecookies";

type PropsSEO = {
  metas?: ISEO;
};
export const SEO: React.FC<PropsSEO> = (props: PropsSEO) => {
  const { settings } = useStaticQuery(graphql`
    query SEO {
      settings: sanitySetting {
        socialLinks
        legalName
        siteUrl
      }
    }
  `);
  if (!props.metas?.seo?.title || !props.metas?.language)
    return <DoNotMountComponent sms={"SEO: title, slug or language is missing"} log={props} />;
  const { seo, language } = props.metas;
  const slug = props.metas?.slug || "/";

  const siteUrl = settings?.siteUrl ? settings.siteUrl : SITE_URL;
  const thumbnailUrlBase = `${siteUrl}assets/thumbnails/`;
  const pageAbsoluteUrl = `${getAbsoluteUrl(getSlugWithLang(slug, language))}`;

  const title = seo?.title || titleDefault;
  const description = seo?.description || descriptionDefault;
  const robots = seo?.robots || "index, follow";

  const canonical = seo?.canonical ?? pageAbsoluteUrl;

  const thumbnail =
    seo?.thumbnail && typeof seo.thumbnail === "object" && seo.thumbnail.asset?.publicUrl
      ? getAbsoluteUrl(seo.thumbnail.asset.publicUrl) || getAbsoluteUrl("index.png", thumbnailUrlBase)
      : (typeof seo.thumbnail === "string" && getAbsoluteUrl(seo?.thumbnail, thumbnailUrlBase)) ||
        getAbsoluteUrl("index.png", thumbnailUrlBase);

  const hrefLangList = props.metas?.hrefLangList?.map((item, index) => {
    const url = getAbsoluteUrl(getSlugWithLang(item?.slug || "/", item.language));
    if (item.language === DEFAULT_LANGUAGE) {
      return (
        <>
          <link key={index} rel="alternate" href={cleanSlashInPath(`${url}`)} hrefLang="x-default" />;
          <link key={index} rel="alternate" href={cleanSlashInPath(`${url}`)} hrefLang={`${item.language}`} />
        </>
      );
    }
    return <link key={index} rel="alternate" href={cleanSlashInPath(`${url}`)} hrefLang={`${item.language}`} />;
  });

  return (
    <>
      {getCodeLang(language) && <html lang={getCodeLang(language)} dir="ltr" />}
      <meta name="description" content={description} />
      <meta name="image" content={thumbnail} />

      <meta property="og:url" content={pageAbsoluteUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={thumbnail} />
      {settings?.socialLinks?.map((link: string, index: number) => (
        <React.Fragment key={`socialLink_${index}`}>
          {getSocialMetaTag(link, thumbnail, props.metas?.seo?.title, props.metas?.seo?.description)}
        </React.Fragment>
      ))}
      {hrefLangList}
      <link rel="canonical" href={`${canonical}`} />
      <title>{title}</title>
      <meta name="robots" content={robots} />
    </>
  );
};
